import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api.ts';
import { useAuth } from '../auth/authStore.ts';

type Access = { userId: string; plan: 'free' | 'plus'; validUntil: string | null };
type Status = 'loading' | 'free' | 'plus' | 'unavailable';

/** Never persisted in local data or restored from a backup. Recheck before premium actions. */
export function usePlusAccess() {
  const auth = useAuth();
  const identity = !auth.switching && auth.state.status === 'signed-in' ? auth.state.user.id : null;
  const latest = useRef(identity); latest.current = identity;
  const mounted = useRef(false), sequence = useRef(0);
  const [value, setValue] = useState<{ identity: string | null; status: Status; until: number }>({ identity: null, status: 'free', until: 0 });
  const refresh = useCallback(async () => {
    const ticket = ++sequence.current;
    if (!identity) return false;
    try {
      const result = await api<Access>('/api/billing/access');
      if (!mounted.current || latest.current !== identity || ticket !== sequence.current) return false;
      const until = result.validUntil ? Date.parse(result.validUntil) : 0;
      if (result.userId !== identity || !['free', 'plus'].includes(result.plan) ||
          (result.plan === 'plus' && !Number.isFinite(until))) throw new Error('Invalid access');
      const plus = result.plan === 'plus' && until > Date.now();
      setValue({ identity, status: plus ? 'plus' : 'free', until: plus ? until : 0 });
      return plus;
    } catch {
      if (mounted.current && latest.current === identity && ticket === sequence.current) setValue({ identity, status: 'unavailable', until: 0 });
      return false;
    }
  }, [identity]);
  useEffect(() => {
    mounted.current = true;
    void refresh();
    const offline = () => { ++sequence.current; setValue({ identity, status: 'unavailable', until: 0 }); };
    const recheck = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('online', recheck); window.addEventListener('offline', offline);
    window.addEventListener('focus', recheck); document.addEventListener('visibilitychange', recheck);
    return () => {
      mounted.current = false; ++sequence.current;
      window.removeEventListener('online', recheck); window.removeEventListener('offline', offline);
      window.removeEventListener('focus', recheck); document.removeEventListener('visibilitychange', recheck);
    };
  }, [identity, refresh]);
  useEffect(() => {
    if (value.status !== 'plus') return;
    // Refresh at least once a minute while open; expire access even if the next request hangs.
    const timer = window.setTimeout(() => {
      if (value.until <= Date.now()) setValue(v => ({ ...v, status: 'free', until: 0 }));
      void refresh();
    }, Math.max(0, Math.min(60_000, value.until - Date.now())));
    return () => window.clearTimeout(timer);
  }, [value, refresh]);
  const status: Status = !identity ? (auth.state.status === 'guest' ? 'free' : 'unavailable')
    : value.identity !== identity ? 'loading' : value.status === 'plus' && value.until <= Date.now() ? 'free' : value.status;
  return { status, allowed: status === 'plus', refresh };
}
